/**
 * Learner session state for every /learn route.
 *
 * Holds three distinct things the UI must tell apart:
 *   - `loading`  — Firebase has not reported yet; render nothing decisive, or a
 *                  signed-out flash appears on every reload for signed-in users.
 *   - `user`     — a Firebase identity exists (may still be unverified).
 *   - `profile`  — the server-side LearnerAccount, i.e. the actor id. Absent
 *                  while unverified or disabled, because the API refuses those.
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import { onLearnAuthChanged, signOutLearner, isLearnAuthConfigured } from '../../services/learnFirebase';
import {
  fetchLearnerProfile,
  redeemSchoolCode,
  LearnerApiError,
  type LearnerProfile,
} from '../../services/learnerAPI';
import {
  readPendingSchoolCode,
  clearPendingSchoolCode,
  capturePendingSchoolCodeFromUrl,
} from './pendingSchoolCode';

export type LearnerBlockReason = 'unverified' | 'pending' | 'disabled' | null;

interface LearnAuthState {
  loading: boolean;
  user: User | null;
  profile: LearnerProfile | null;
  blocked: LearnerBlockReason;
  configured: boolean;
  /** Why the last school code was refused, in words a learner can act on. */
  schoolCodeError: string | null;
  /** Join a school, then reload the account. Resolves true when the code was accepted. */
  joinSchool: (code: string) => Promise<boolean>;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const LearnAuthContext = createContext<LearnAuthState | null>(null);

export function LearnAuthProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<LearnerProfile | null>(null);
  const [blocked, setBlocked] = useState<LearnerBlockReason>(null);
  const [schoolCodeError, setSchoolCodeError] = useState<string | null>(null);

  const redeem = useCallback(async (code: string): Promise<boolean> => {
    try {
      await redeemSchoolCode(code);
      clearPendingSchoolCode();
      setSchoolCodeError(null);
      return true;
    } catch (error) {
      // An unconfirmed email cannot redeem yet — keep the code for when it can.
      if (error instanceof LearnerApiError && error.code === 'LEARNER_EMAIL_UNVERIFIED') return false;
      clearPendingSchoolCode();
      setSchoolCodeError(
        error instanceof LearnerApiError ? error.message : 'Could not check that school code. Try again.',
      );
      return false;
    }
  }, []);

  const loadProfile = useCallback(async (current: User | null) => {
    if (!current) {
      setProfile(null);
      setBlocked(null);
      return;
    }
    // Before the profile, not after: a valid code turns a pending account into
    // an approved one, and the profile call is what reports which it is.
    const pendingCode = readPendingSchoolCode();
    if (pendingCode) await redeem(pendingCode);
    try {
      setProfile(await fetchLearnerProfile());
      setBlocked(null);
    } catch (error) {
      setProfile(null);
      if (error instanceof LearnerApiError) {
        if (error.code === 'LEARNER_EMAIL_UNVERIFIED') setBlocked('unverified');
        else if (error.code === 'LEARNER_PENDING') setBlocked('pending');
        else if (error.code === 'LEARNER_DISABLED') setBlocked('disabled');
        else setBlocked(null);
      }
    }
  }, [redeem]);

  useEffect(() => {
    capturePendingSchoolCodeFromUrl();
    return onLearnAuthChanged(async (next) => {
      setUser(next);
      await loadProfile(next);
      setLoading(false);
    });
  }, [loadProfile]);

  const refresh = useCallback(async () => {
    // Force a token refresh first: right after verifying an email the cached
    // token still says email_verified=false, so the server would keep refusing.
    await user?.reload();
    await user?.getIdToken(true);
    await loadProfile(user);
  }, [user, loadProfile]);

  const joinSchool = useCallback(
    async (code: string) => {
      const joined = await redeem(code);
      if (joined) await loadProfile(user);
      return joined;
    },
    [redeem, loadProfile, user],
  );

  const signOut = useCallback(async () => {
    await signOutLearner();
    setUser(null);
    setProfile(null);
    setBlocked(null);
    setSchoolCodeError(null);
  }, []);

  return (
    <LearnAuthContext.Provider
      value={{
        loading,
        user,
        profile,
        blocked,
        configured: isLearnAuthConfigured,
        schoolCodeError,
        joinSchool,
        refresh,
        signOut,
      }}
    >
      {children}
    </LearnAuthContext.Provider>
  );
}

export function useLearnAuth(): LearnAuthState {
  const ctx = useContext(LearnAuthContext);
  if (!ctx) throw new Error('useLearnAuth must be used inside <LearnAuthProvider>');
  return ctx;
}
